import {FormEvent, useEffect, useRef, useState} from "react";
import {Alert, Button, Card, Form} from "react-bootstrap";
import {Link, Navigate, useSearchParams} from "react-router";
import {useAuth} from "../auth/AuthContext";
import {AuthApi} from "../api/AuthApi";
import {ApiError} from "../api/http";
import {MfaChallenge, MfaMethod} from "../api/models";
import {auth, rich} from "../i18n";
import {ErrorAlert} from "../components/ErrorAlert";
import {SubmitButton} from "../components/SubmitButton";
import {LoadingSpinner} from "../components/LoadingSpinner";
import {usePageTitle} from "../components/usePageTitle";
import {useTurnstile} from "../components/useTurnstile";
import {takeSessionEnded} from "../auth/sessionEnded";

// Only paths on this site, so a crafted link can't send people elsewhere after signing in.
function safeReturnTo(value: string | null): string {
    return value && value.startsWith('/') && !value.startsWith('//') ? value : '/account/profile';
}

// The one sign-in screen, for the account pages and the administration area alike. Each try passes Cloudflare
// Turnstile's bot check first.
export function LoginPage() {
    const {user, loading, login} = useAuth();
    const [params] = useSearchParams();
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<unknown>();
    const [challenge, setChallenge] = useState<MfaChallenge | null>(null);
    const [expired, setExpired] = useState(false);
    // Signed out because the API ended the session (auth/sessionEnded.ts): said once, on arriving here.
    const [sessionEnded] = useState(takeSessionEnded);
    const turnstile = useTurnstile();
    usePageTitle(challenge ? auth.mfa.title : auth.signIn.title);

    if (user) {
        return <Navigate to={safeReturnTo(params.get('returnTo'))} replace/>;
    }
    if (loading) {
        return <LoadingSpinner/>;
    }

    const submit = async (e: FormEvent) => {
        e.preventDefault();
        setBusy(true);
        setError(undefined);
        setExpired(false);
        const turnstileToken = await turnstile.waitForToken();
        if (!turnstileToken) {
            setBusy(false);
            return;
        }
        try {
            const result = await login(username.trim(), password, turnstileToken);
            if (result) {
                setChallenge(result);
                setBusy(false);
            }
        } catch (err) {
            setError(err);
            setBusy(false);
            turnstile.reset();
        }
    };

    const restart = (timedOut: boolean) => {
        setChallenge(null);
        setPassword('');
        setExpired(timedOut);
        turnstile.reset();
    };

    if (challenge) {
        return <MfaStep challenge={challenge} onRestart={restart}/>;
    }

    return (
        <Card className="auth-card mx-auto mt-md-4">
            <Card.Body className="p-4">
                <h1 className="h4 mb-4">{auth.signIn.title}</h1>
                {expired && <Alert variant="warning">{auth.signIn.expired}</Alert>}
                {sessionEnded && !expired && <Alert variant="warning">{auth.signIn.sessionEnded}</Alert>}
                <ErrorAlert error={error} messages={{ 400: auth.signIn.failed, 429: auth.signIn.tooMany }}/>
                <Form onSubmit={submit}>
                    <Form.Group className="mb-3" controlId="username">
                        <Form.Label>{auth.signIn.username}</Form.Label>
                        <Form.Control autoComplete="username" autoFocus required value={username} onChange={e => setUsername(e.target.value)}/>
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="password">
                        <Form.Label>{auth.signIn.password}</Form.Label>
                        <Form.Control type="password" autoComplete="current-password" required value={password} onChange={e => setPassword(e.target.value)}/>
                    </Form.Group>
                    <div className="mb-3">
                        {turnstile.widget}
                        {turnstile.failed && <Alert variant="warning">{auth.signIn.turnstileUnavailable}</Alert>}
                    </div>
                    <SubmitButton busy={busy} className="w-100">{auth.signIn.submit}</SubmitButton>
                </Form>
                <div className="text-center mt-3">
                    <Link to="/forgot-password" className="small">{auth.signIn.forgotPassword}</Link>
                </div>
            </Card.Body>
        </Card>
    );
}

const codeErrors = {
    400: auth.mfa.codeIncorrect,
    429: auth.mfa.tooManyAttempts
};

// Sign-in, second step: a code from one of the account's MFA methods.
function MfaStep({challenge, onRestart}: { challenge: MfaChallenge, onRestart(timedOut: boolean): void }) {
    const {completeMfa} = useAuth();
    // A code emailed to an address that bounces won't arrive: start with another way, when there is one.
    const [method, setMethod] = useState<MfaMethod>(
        (challenge.emailUndeliverable && challenge.methods.find(x => x !== 'email')) || challenge.methods[0]);
    const [code, setCode] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<unknown>();
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const autoSent = useRef(false);

    const handleError = (err: unknown) => {
        if (err instanceof ApiError && err.status === 401) {
            onRestart(true);
        } else {
            setError(err);
        }
    };

    const sendEmailCode = async () => {
        setSending(true);
        setError(undefined);
        try {
            await AuthApi.sendMfaEmailCode(challenge.mfaToken);
            setSent(true);
        } catch (err) {
            handleError(err);
        } finally {
            setSending(false);
        }
    };

    // Choosing email means wanting a code: send the first one without another click.
    useEffect(() => {
        if (method === 'email' && !autoSent.current) {
            autoSent.current = true;
            void sendEmailCode();
        }
    }, [method]);

    const submit = async (e: FormEvent) => {
        e.preventDefault();
        setBusy(true);
        setError(undefined);
        try {
            await completeMfa(challenge, method, code.trim());
            // Signed in: LoginPage re-renders and navigates away.
        } catch (err) {
            handleError(err);
            setBusy(false);
        }
    };

    const switchTo = (next: MfaMethod) => {
        setMethod(next);
        setCode('');
        setError(undefined);
    };

    const isRecovery = method === 'recovery';
    return (
        <Card className="auth-card mx-auto mt-md-4">
            <Card.Body className="p-4">
                <h1 className="h4 mb-3">{auth.mfa.title}</h1>
                <p className="text-body-secondary">{auth.mfa.prompts[method]}</p>
                <ErrorAlert error={error} messages={codeErrors}/>
                {method === 'email' && challenge.emailUndeliverable && (
                    <Alert variant="warning">{rich(auth.mfa.emailUndeliverable, { link: text => <Link to="/support/new">{text}</Link> })}</Alert>
                )}
                {method === 'email' && sent && <Alert variant="info">{auth.mfa.emailSent}</Alert>}
                <Form onSubmit={submit}>
                    <Form.Group className="mb-3" controlId="mfa-code">
                        <Form.Label>{isRecovery ? auth.mfa.recoveryCode : auth.mfa.code}</Form.Label>
                        <Form.Control key={method} autoFocus required value={code} onChange={e => setCode(e.target.value)}
                                      autoComplete={isRecovery ? 'off' : 'one-time-code'} inputMode={isRecovery ? 'text' : 'numeric'}
                                      maxLength={isRecovery ? 11 : 6} placeholder={isRecovery ? 'xxxxx-xxxxx' : '123456'}
                                      className="font-monospace"/>
                    </Form.Group>
                    <SubmitButton busy={busy} className="w-100">{auth.mfa.submit}</SubmitButton>
                </Form>
                {method === 'email' && (
                    <div className="text-center mt-2">
                        <Button variant="link" size="sm" disabled={sending} onClick={sendEmailCode}>
                            {sending ? auth.mfa.sending : sent ? auth.mfa.sendNewCode : auth.mfa.sendCode}
                        </Button>
                    </div>
                )}
                <div className="d-flex flex-column align-items-center mt-3 small">
                    {challenge.methods.filter(x => x !== method).map(x => (
                        <Button key={x} variant="link" size="sm" onClick={() => switchTo(x)}>{auth.mfa.switchTo[x]}</Button>
                    ))}
                    <Button variant="link" size="sm" className="text-body-secondary" onClick={() => onRestart(false)}>{auth.mfa.signInAsSomeoneElse}</Button>
                </div>
            </Card.Body>
        </Card>
    );
}
